import React from 'react';
import { Paths } from '../../urls/Paths';
import { CmsAsset } from '../../integrations/contentful/types';
import { formatDateRange } from '../../utils/date';
import {
  baseXInPixels,
  baseXUnit,
  color,
  imageHoverOpacity,
  linkStyle
} from '../../styles/theme';
import RouteLink from '../navigation/RouteLink';
import Paragraph from '../elements/Paragraph';
import CmsMedia from '../cms/CmsMedia';
import HyperLink from '../elements/HyperLink';

interface MemoryAuthorInfo {
  author: {
    name: string;
    picture?: CmsAsset;
    slug: string;
  };
  memoryStartDate: string;
  memoryEndDate: string;
  noLink?: boolean;
  subtitle?: string;
}

const imageSize = baseXInPixels(4);
const MemoryAuthorInfo: React.FunctionComponent<MemoryAuthorInfo> = ({
  author,
  memoryStartDate,
  memoryEndDate,
  noLink,
  subtitle
}) => {
  const startDate = new Date(memoryStartDate);
  const endDate = new Date(memoryEndDate);

  function renderContent(noLink: boolean) {
    return (
      <div className="memory-author-info">
        {!subtitle && (
          <div className="traveller-picture">
            {author.picture ? (
              <CmsMedia
                media={author.picture}
                width={imageSize}
                height={imageSize}
                borderRadius={7}
                crop
              />
            ) : (
              <div className="placeholder" />
            )}
          </div>
        )}
        <div className="author-details">
          {subtitle ? (
            <div data-testid="memory-subtitle-container">
              <Paragraph data-cy="memory-subtitle">
                <span className="subtitle">{subtitle}</span>
              </Paragraph>
            </div>
          ) : (
            <>
              <Paragraph data-cy="memory-author-name">
                <span className="author-name">{author.name}</span>
              </Paragraph>
              <Paragraph small data-cy="memory-author-date">
                {formatDateRange(startDate, endDate)}
              </Paragraph>
            </>
          )}
        </div>
        {/*language=CSS*/}
        <style jsx>{`
          .memory-author-info {
            display: flex;
            align-items: flex-start;
          }
          .traveller-picture {
            flex-shrink: 0;
            margin-right: ${baseXUnit(1)};
            width: ${imageSize}px;
            height: ${imageSize}px;
          }
          .placeholder {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: ${color.lightBackground};
          }
          .author-details {
            flex-grow: 1;
            min-width: 0; /* Allow text to wrap/shrink */
          }
          .author-details > :global(div:first-child) {
            line-height: ${baseXUnit(2)};
          }
          /* Add hover styles only to the author name and image. */
          .memory-author-info .author-name,
          .memory-author-info .subtitle {
            ${linkStyle.withAnimation}
          }
          .memory-author-info:hover .author-name,
          .memory-author-info:hover .subtitle {
            ${noLink ? '' : linkStyle.hover};
          }
          .memory-author-info:active .author-name,
          .memory-author-info:active .subtitle {
            ${noLink ? '' : linkStyle.active}
          }
          .memory-author-info:hover :global(img) {
            ${noLink ? '' : `opacity: ${imageHoverOpacity};`}
          }
          .subtitle {
            color: ${color.gray};
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      {noLink ? (
        renderContent(true)
      ) : (
        <RouteLink
          path={Paths.Traveller}
          params={{
            slug: author.slug
          }}
        >
          <HyperLink data-testid="traveller-link">
            {renderContent(false)}
          </HyperLink>
        </RouteLink>
      )}
    </>
  );
};

export default MemoryAuthorInfo;
